import type { AppointmentPrintLocationResult } from "@ascleo/frontdesk-contracts/appointment-printing/model";
import type { AppointmentPrintSnapshot } from "@ascleo/frontdesk-contracts/appointment-printing/native";
import { formatUtcInstantForLocale } from "@ascleo/types/date-time-validity";

/** Inputs captured from the confirmed booking before a print attempt starts. @since 1.2.0 @example `const booking: ConfirmedAppointmentPrintBooking = { scheduleId: "s1", patientId: "p1", patientDisplayName: "Erika Muster", startTime: "2026-09-17T08:00:00.000Z", endTime: "2026-09-17T08:30:00.000Z" };` */
export interface ConfirmedAppointmentPrintBooking {
  readonly scheduleId: string;
  readonly patientId: string;
  readonly patientDisplayName: string;
  readonly startTime: string;
  readonly endTime: string;
}

/** Localized non-patient labels passed to the native renderer. @since 1.2.0 @example `const labels: AppointmentSlipLabels = { title: "Ihr Termin", patient: "Patient", appointment: "Termin", location: "Ort", mapHint: "Standort öffnen" };` */
export interface AppointmentSlipLabels {
  readonly title: string;
  readonly patient: string;
  readonly appointment: string;
  readonly location: string;
  readonly mapHint: string;
}

/** Build the immutable, allowlisted native snapshot from confirmed server truth. @since 1.2.0 @example `buildAppointmentPrintSnapshot(input);` */
export function buildAppointmentPrintSnapshot(input: {
  readonly attemptId: string;
  readonly deadlineAt: number;
  readonly now: number;
  readonly booking: ConfirmedAppointmentPrintBooking;
  readonly practiceDisplayName: string;
  readonly location: AppointmentPrintLocationResult;
  readonly locale: string;
  readonly labels: AppointmentSlipLabels;
}): AppointmentPrintSnapshot {
  const available = input.location.outcome === "available" ? input.location : undefined;
  const contactLines = available === undefined
    ? []
    : [available.contact.phone, available.contact.email].filter((value): value is string => value !== undefined);
  return {
    attemptId: input.attemptId,
    remainingDeadlineMs: Math.max(1, Math.min(300_000, input.deadlineAt - input.now)),
    patientDisplayName: input.booking.patientDisplayName,
    appointmentDate: formatUtcInstantForLocale(input.booking.startTime, input.locale, {
      timeZone: "Europe/Berlin",
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    }),
    appointmentTime: formatUtcInstantForLocale(input.booking.startTime, input.locale, {
      timeZone: "Europe/Berlin",
      hour: "2-digit",
      minute: "2-digit",
    }),
    practiceDisplayName: available?.location.name ?? input.practiceDisplayName,
    contactLines,
    addressLines: available?.location.addressLines ?? [],
    ...(available?.coordinates === undefined ? {} : { coordinates: available.coordinates }),
    labels: input.labels,
  };
}
